import React, {FC, memo, useMemo} from "react";
import {Alert, Card, Empty, Tag} from "antd";
import moment from "moment";
import {HistoryOutlined} from "@ant-design/icons";
import {BookingHistoryByIdWithBookingInfoQuery, BookingHistoryByCourseIdQuery} from "../../generated/graphql";
import {renderUser} from "./bookingHistoryRenderHelpers";
import {parseDescription} from "./bookingHistoryDescription";
import {extractTransferContext} from "./transferContext";
import {useCurrency} from "../../helpers/currencyHelper";
import {renderAmount} from "../../helpers/renderAmount";
import useHighestRole from "../../helpers/useHighestRole";
import Roles from "../../../shared/models/roles";
import {atLeast} from "../common/AuthenticateSetup";
import {
    HistoryRenderOptions,
    MONEY_FIELDS,
    BOOKING_MONEY_FIELDS,
    HIDDEN_FIELDS,
    BOOKING_HIDDEN_FIELDS,
    FIELD_LABELS,
    renderJsonValue,
} from "../common/history/historyDiffHelpers";
import CombinedCourseHistoryDrawer from "../courseHistory/CombinedCourseHistoryDrawer";

/**
 * Detail-Komponente fuer einen einzelnen Booking-History-Eintrag.
 *
 * Stand vor Ueberarbeitung: 80 Zeilen mit englischen Labels, einem
 * "€"-Render-Bug und einem irrefuehrenden Hinweis "Keine weiteren
 * Informationen ueber den Benutzer verfuegbar", wenn `item.user` leer war.
 *
 * Neu (Stufe 1 + T1 des Booking-History-Plans):
 * - 2-spaltiges Vorher/Nachher-Layout analog CourseHistoryDetailsModal
 * - Wiederverwendung der gemeinsamen Diff-Helfer aus
 *   `containers/common/history/historyDiffHelpers.tsx`
 * - Spezial-Karte fuer scheduledPayments-Arrays (kompakte Liste mit
 *   paymentDue/paymentAmount/paymentMethod)
 * - Erkennung von Course-Edit-Trigger ueber den Description-Suffix
 *   " (durch Kursanpassung)" mit Hinweis-Alert + Quicklink in die
 *   Kurshistorie
 * - Edge-Cases:
 *   - oldValues fehlt (z.B. Created, BookingSettle) -> nur Nachher-Spalte
 *   - newValues fehlt (z.B. BookingDelete) -> nur Vorher-Spalte
 *   - Beide leer -> Header-only Anzeige, KEIN irrefuehrender Hinweis
 */

type BookingHistoryItem =
    | BookingHistoryByIdWithBookingInfoQuery["bookingHistoryByIdWithBookingInfo"][number]
    | BookingHistoryByCourseIdQuery["bookingHistoryByCourseId"][number];

type BookingHistoryDetailsProps = {
    item: BookingHistoryItem;
};

/**
 * Repraesentiert ein einzelnes scheduled-payment-Snapshot, wie es in
 * oldValues/newValues vorkommen kann. Die Felder sind bewusst als
 * `unknown`/optional typisiert, weil das JSONB-Schema nicht streng
 * validiert ist und einzelne Events nur Teilmengen schreiben.
 *
 * Achtung: `paymentMethod` kann je nach Quelle entweder ein
 * lesbarer String (z.B. "Lastschrift") ODER ein vollstaendiges
 * PaymentMethod-Objekt (vom Server-Snapshot) sein. Daher hier
 * `unknown` - wir validieren mit Type-Guards bei Render-Zeit.
 */
type ScheduledPaymentSnapshot = {
    id?: string;
    paymentAmount?: number | string | null;
    paymentDue?: string | null;
    paymentMethod?: unknown;
    paymentMethodTypeName?: string | null;
    paymentMethodType?: string | null;
    paymentType?: string | null;
    paid?: boolean | null;
    // Tombstone-Marker (B-223): Wenn gesetzt, wurde der Posten geloescht.
    // Snapshots aus `logBookingChange` (z.B. PriceChanged via Kursanpassung)
    // serialisieren die volle ScheduledPayment-Entitaet inkl. deletedAt.
    // In der Vorher/Nachher-Darstellung wuerden tombstoned Eintraege die
    // sichtbare Postensumme von der angezeigten `bookingGrossPrice`
    // entkoppeln (Beispiel: 6 Posten in der Liste, aber Bruttopreis nur
    // ueber 5 aktive Posten gerechnet) — daher hier ausfiltern.
    deletedAt?: string | Date | null;
};

/**
 * Filtert tombstoned (geloeschte) Posten aus einem Snapshot-Array.
 * Tombstones bleiben in der DB und damit auch im History-Snapshot bestehen,
 * sind fuer den Nutzer in der Vorher/Nachher-Ansicht aber irrefuehrend, weil
 * `bookingGrossPrice` ohne sie berechnet wurde.
 */
const filterTombstonedPayments = (
    payments: ScheduledPaymentSnapshot[] | null | undefined,
): ScheduledPaymentSnapshot[] | null => {
    if (!payments) {
        return null;
    }
    return payments.filter((p) => !p.deletedAt);
};

/**
 * Felder, die wir aus oldValues/newValues bewusst NICHT in der generischen
 * Vorher/Nachher-Liste rendern, weil sie ueber dedizierte Spezial-Karten
 * (z.B. scheduledPayments) angezeigt werden.
 */
const SPECIAL_FIELDS_RENDERED_SEPARATELY: ReadonlyArray<string> = ["scheduledPayments"];

/**
 * Pruefe ob zwei Werte aus History-Snapshots als "gleich" zu betrachten
 * sind. Wir nutzen JSON-Stringify als pragmatischen Tiefenvergleich -
 * die Snapshots sind reines JSON.
 */
const areValuesEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) {
        return true;
    }
    if (a === undefined || b === undefined) {
        // Ein Wert nur in einer Seite vorhanden ist eine Aenderung
        return false;
    }
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
};

/**
 * Reduziert ein Snapshot-Objekt auf die Felder, die wir generisch rendern.
 * - Versteckte Felder werden entfernt
 * - Felder mit dedizierter Karte (scheduledPayments) werden entfernt
 * - Felder, die zwischen old und new identisch sind, werden entfernt
 *   (Default: weglassen statt ausgrauen)
 */
const filterChangedFields = (
    snapshot: Record<string, unknown> | undefined,
    counterpart: Record<string, unknown> | undefined,
    hiddenFields: ReadonlyArray<string>,
): Record<string, unknown> => {
    if (!snapshot) {
        return {};
    }
    const result: Record<string, unknown> = {};

    Object.keys(snapshot).forEach((key) => {
        if (hiddenFields.indexOf(key) !== -1) {
            return;
        }
        if (SPECIAL_FIELDS_RENDERED_SEPARATELY.indexOf(key) !== -1) {
            return;
        }
        // Rabatt mit Wert 0 / null / undefined ausblenden — ein Null-Rabatt
        // erzeugt nur visuelles Rauschen ("Rabatt: 0,00 €"). Tatsaechliche
        // Rabatte (> 0) werden weiterhin angezeigt.
        if (key === "discount") {
            const v = snapshot[key];
            if (v === null || v === undefined || v === 0) {
                return;
            }
        }
        // Wenn Counterpart vorhanden, vergleiche und blende identische Felder aus
        if (counterpart && Object.prototype.hasOwnProperty.call(counterpart, key)) {
            if (areValuesEqual(snapshot[key], counterpart[key])) {
                return;
            }
        }
        result[key] = snapshot[key];
    });

    return result;
};

/**
 * Kompakte Karte zur Anzeige eines scheduledPayments-Arrays
 * (Vorher- oder Nachher-Seite).
 */
type ScheduledPaymentsCardProps = {
    title: string;
    payments: ScheduledPaymentSnapshot[] | undefined | null;
    currencyShort: string;
    /** Farbton fuer den Header-Tag (z.B. "red" fuer geloeschte, "green" fuer neue Eintraege). */
    accentColor?: "default" | "red" | "green" | "blue";
    /**
     * Render-Modus:
     * - "diff": kompakte Liste ohne Spalten-Header (innerhalb 2-Spalten Vorher/Nachher).
     * - "single-action-add": Spalten-Header + gruener linker Akzent je Zeile, fuer Single-Sided
     *   "Posten erstellt"-Events.
     * - "single-action-remove": Spalten-Header + roter linker Akzent je Zeile, fuer Single-Sided
     *   "Posten geloescht"-Events.
     * - "single-action-info": Spalten-Header + blauer linker Akzent je Zeile, fuer
     *   Single-Sided Marker-Aenderungen ohne Wertaenderung
     *   (z.B. "Schutz vor Neuberechnung aufgehoben").
     */
    mode?: "diff" | "single-action-add" | "single-action-remove" | "single-action-info";
};

const ScheduledPaymentsCard: FC<ScheduledPaymentsCardProps> = ({
    title,
    payments,
    currencyShort,
    accentColor = "default",
    mode = "diff",
}) => {
    if (!payments || payments.length === 0) {
        return null;
    }

    // Posten chronologisch nach Faelligkeit sortieren. Snapshots aus
    // `logBookingChange` spiegeln die TypeORM-Lade-Reihenfolge wider, was
    // visuell willkuerlich wirkt — eine zeitliche Sortierung macht die
    // Vorher/Nachher-Listen direkt vergleichbar. Eintraege ohne paymentDue
    // wandern ans Ende.
    const sortedPayments = [...payments].sort((a, b) => {
        const aTime = a.paymentDue ? new Date(a.paymentDue).getTime() : Number.POSITIVE_INFINITY;
        const bTime = b.paymentDue ? new Date(b.paymentDue).getTime() : Number.POSITIVE_INFINITY;
        if (Number.isNaN(aTime) && Number.isNaN(bTime)) {
            return 0;
        }
        if (Number.isNaN(aTime)) {
            return 1;
        }
        if (Number.isNaN(bTime)) {
            return -1;
        }
        return aTime - bTime;
    });

    const isSingleAction = mode === "single-action-add" || mode === "single-action-remove" || mode === "single-action-info";
    // AntD v4 Standard-Farbtokens: green-6 #52c41a, red-5 #ff4d4f, blue-6 #1890ff
    const rowAccentColor =
        mode === "single-action-add" ? "#52c41a" :
        mode === "single-action-remove" ? "#ff4d4f" :
        mode === "single-action-info" ? "#1890ff" :
        null;

    const formatPaymentAmount = (amount: number | string | null | undefined): string => {
        if (amount === null || amount === undefined) {
            return "-";
        }
        const numeric = typeof amount === "string" ? Number(amount) : amount;
        if (Number.isNaN(numeric)) {
            return String(amount);
        }
        return renderAmount({amount: numeric, currency: currencyShort as "EUR" | "CHF" | "USD" | "GBP"});
    };

    const formatPaymentDue = (paymentDue: string | null | undefined): string => {
        if (!paymentDue) {
            return "-";
        }
        const m = moment(paymentDue);
        if (!m.isValid()) {
            return String(paymentDue);
        }
        return m.format("DD.MM.YYYY");
    };

    /**
     * Bevorzugt einen lesbaren String. Type-Guard: falls einer der Werte
     * - z.B. `paymentMethod` - kein String ist (etwa ein vollstaendiges
     * PaymentMethod-Objekt vom Server-Snapshot mit Feldern wie
     * `iban`, `type`, `bookerId` etc.), wird er ignoriert.
     *
     * Hintergrund: Im Course-Drawer-Kontext landen bei manchen Events
     * komplette Objekte in `payment.paymentMethod`. Vor dem Type-Guard
     * fuehrte das zu einem React-Crash ("Objects are not valid as a
     * React child").
     */
    const formatPaymentMethod = (payment: ScheduledPaymentSnapshot): string => {
        const candidates: ReadonlyArray<unknown> = [
            payment.paymentMethodTypeName,
            payment.paymentMethod,
            payment.paymentMethodType,
            payment.paymentType,
        ];
        for (const candidate of candidates) {
            if (typeof candidate === "string" && candidate !== "") {
                return candidate;
            }
        }
        return "";
    };

    return (
        <Card
            size="small"
            title={
                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                    <Tag color={accentColor} style={{margin: 0}}>{title}</Tag>
                    <span style={{fontWeight: 400, color: "#666"}}>
                        ({payments.length} {payments.length === 1 ? "Posten" : "Posten"})
                    </span>
                </div>
            }
            style={{marginTop: 8}}
            bodyStyle={{padding: "8px 12px"}}
        >
            <div style={{fontSize: "0.85em"}}>
                {/* Spalten-Header nur im Single-Action-Modus, damit der Diff-Modus
                    in der kompakten 2-Spalten-Ansicht nicht ueberladen wirkt. */}
                {isSingleAction && (
                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                            padding: "4px 0 4px 11px",
                            borderBottom: "1px solid #f0f0f0",
                            color: "#999",
                            fontSize: "0.9em",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: 0.3,
                        }}
                    >
                        <span style={{minWidth: 90}}>Fällig am</span>
                        <span style={{minWidth: 80}}>Betrag</span>
                        <span style={{flex: 1}}>Zahlungsart</span>
                        <span>Status</span>
                    </div>
                )}
                {sortedPayments.map((payment, idx) => {
                    const method = formatPaymentMethod(payment);

                    return (
                        <div
                            key={payment.id ?? idx}
                            style={{
                                display: "flex",
                                gap: 8,
                                padding: isSingleAction ? "6px 0 6px 8px" : "4px 0",
                                borderBottom: idx < sortedPayments.length - 1 ? "1px dashed #f0f0f0" : "none",
                                borderLeft: rowAccentColor ? `3px solid ${rowAccentColor}` : "none",
                                alignItems: "center",
                            }}
                        >
                            <span style={{minWidth: 90, color: "#666"}}>{formatPaymentDue(payment.paymentDue)}</span>
                            <span style={{minWidth: 80, fontWeight: 500}}>{formatPaymentAmount(payment.paymentAmount)}</span>
                            {/* method ist durch formatPaymentMethod garantiert ein String. */}
                            {/* Zusaetzlich defensiv typeof-Check, damit auch bei zukuenftigen */}
                            {/* Refactorings keine Objekte in JSX landen koennen (siehe Bug 1). */}
                            {isSingleAction ? (
                                <span style={{flex: 1, color: method ? "#999" : "#ccc"}}>
                                    {typeof method === "string" && method !== "" ? method : "—"}
                                </span>
                            ) : (
                                typeof method === "string" && method !== "" && (
                                    <span style={{color: "#999"}}>{method}</span>
                                )
                            )}
                            {/* Status-Spalte:
                                - Diff-Modus: nur "Bezahlt"-Tag wenn paid (rechtsbuendig via auto-margin).
                                - Single-Action: stets ein Tag (Bezahlt/Unbezahlt) — der Add/Remove-Kontext
                                  ist bereits durch Card-Titel + linken Border klar; ein zusaetzliches
                                  "Neu"/"Entfernt"-Tag wuerde mit "Bezahlt" konkurrieren. */}
                            {isSingleAction ? (
                                payment.paid === true ? (
                                    <Tag color="green" style={{margin: 0}}>Bezahlt</Tag>
                                ) : (
                                    <Tag style={{margin: 0}}>Unbezahlt</Tag>
                                )
                            ) : (
                                payment.paid === true && (
                                    <Tag color="green" style={{margin: 0, marginLeft: "auto"}}>Bezahlt</Tag>
                                )
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

const BookingHistoryDetails: FC<BookingHistoryDetailsProps> = ({item}) => {
    const currencySettings = useCurrency();
    // Admin und hoeher haben Zugriff auf den Kurshistorie-Tab im
    // CombinedCourseHistoryDrawer (hierarchie-basiert, deckungsgleich mit dem
    // Backend @Authorized([Admin, Superuser])). Ohne diese Berechtigung wuerde
    // der Drawer in den !hasCourseHistoryAccess-Pfad gehen und einen Drawer mit
    // Titel "Buchungshistorie: <courseNumber>" zeigen - was die Aussage des
    // Buttons "In Kurshistorie ansehen" konterkariert. Daher den Quicklink-Button
    // nur fuer berechtigte Rollen anzeigen.
    const highestRole = useHighestRole();
    const hasCourseHistoryAccess = atLeast(Roles.Admin).includes(highestRole);

    const oldValues = (item.oldValues as Record<string, unknown> | undefined) ?? undefined;
    const newValues = (item.newValues as Record<string, unknown> | undefined) ?? undefined;

    // Nachvollziehbarkeit beim Umbuchen: strukturierter Übertrags-Kontext.
    // direction "in" → neue Buchung (newValues), "out" → alte Buchung (oldValues).
    const transferContext = extractTransferContext(newValues) ?? extractTransferContext(oldValues);

    const userName = renderUser(item.user)?.props.children ?? item.userName ?? "System";
    const {cleanDescription, isCourseEdit} = parseDescription(item.description ?? "");

    // Render-Optionen fuer die geteilten Helfer.
    // BookingHistory kennt keine UUID-Aufloesung (wir haben weder Lookup-Maps
    // fuer paymentMethodId noch fuer attendeeId/bookerId/courseId), nutzt
    // aber Booking-spezifische Hidden-Field- und Money-Field-Listen.
    const renderOptions: HistoryRenderOptions = useMemo(() => ({
        currencySettings,
        moneyFields: [...MONEY_FIELDS, ...BOOKING_MONEY_FIELDS],
        hiddenFields: [...HIDDEN_FIELDS, ...BOOKING_HIDDEN_FIELDS],
        fieldLabels: FIELD_LABELS,
    }), [currencySettings]);

    // Snapshot-Arrays werden um geloeschte (tombstoned) Posten bereinigt:
    // `logBookingChange` serialisiert die volle Booking-Entitaet inkl. aller
    // ScheduledPayments — auch der tombstoned. Da der angezeigte
    // `bookingGrossPrice` die geloeschten nicht mehr enthaelt, wuerde die
    // Vorher/Nachher-Liste sonst eine andere Summe zeigen als der Header
    // (Beispiel B-223: 6 sichtbare Posten = 136 €, Bruttopreis = 125 €).
    const oldScheduledPayments = filterTombstonedPayments(
        oldValues?.scheduledPayments as ScheduledPaymentSnapshot[] | undefined,
    );
    const newScheduledPayments = filterTombstonedPayments(
        newValues?.scheduledPayments as ScheduledPaymentSnapshot[] | undefined,
    );

    // Gefilterte Felder (ohne identische / ohne Spezialfelder)
    const oldFiltered = useMemo(
        () => filterChangedFields(oldValues, newValues, renderOptions.hiddenFields ?? HIDDEN_FIELDS),
        [oldValues, newValues, renderOptions.hiddenFields],
    );
    const newFiltered = useMemo(
        () => filterChangedFields(newValues, oldValues, renderOptions.hiddenFields ?? HIDDEN_FIELDS),
        [oldValues, newValues, renderOptions.hiddenFields],
    );

    const hasOldData = Object.keys(oldFiltered).length > 0 || (oldScheduledPayments && oldScheduledPayments.length > 0);
    const hasNewData = Object.keys(newFiltered).length > 0 || (newScheduledPayments && newScheduledPayments.length > 0);
    const hasAnyData = hasOldData || hasNewData;

    // Single-Sided-Erkennung: nur eine Seite des Diffs hat Daten.
    // Triggert das fokussierte Layout (Posten erstellt / Posten geloescht /
    // BookingCreate / BookingDelete). Der modale Titel beschreibt die Aktion
    // bereits — wir verzichten daher hier auf das Vorher/Nachher-Heading
    // und blenden auch die Description-Doppelung im Meta-Block aus.
    const singleSidedSide: "new" | "old" | null =
        hasNewData && !hasOldData ? "new" : hasOldData && !hasNewData ? "old" : null;

    const currencyCode = currencySettings.short as "EUR" | "CHF" | "USD" | "GBP";

    return (
        <div>
            {/* Meta-Header: Datum + Beschreibung + User */}
            <div style={{marginBottom: 16, padding: "8px 12px", backgroundColor: "#fafafa", borderRadius: 4}}>
                <div style={{fontSize: "0.85em", color: "#666", marginBottom: 4}}>
                    {moment(item.createDate).format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                    {userName && <> &middot; {userName}</>}
                </div>
                {cleanDescription && singleSidedSide === null && (
                    <div style={{fontSize: "0.9em", color: "#333"}}>
                        {cleanDescription}
                    </div>
                )}
            </div>

            {/* Trigger-Hinweis (T1): Aenderung wurde durch Kursanpassung ausgeloest */}
            {isCourseEdit && (
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 16}}
                    message="Diese Änderung wurde durch eine Kursanpassung ausgelöst."
                    action={item.course?.id && hasCourseHistoryAccess ? (
                        <CombinedCourseHistoryDrawer
                            courseId={item.course.id}
                            courseNumber={
                                item.course.courseNumber !== null && item.course.courseNumber !== undefined
                                    ? String(item.course.courseNumber)
                                    : undefined
                            }
                            defaultTab="course"
                            buttonProps={{
                                size: "small",
                                type: "link",
                                children: "In Kurshistorie ansehen",
                                icon: <HistoryOutlined />,
                            }}
                        />
                    ) : undefined}
                />
            )}

            {/* Nachvollziehbarkeit beim Umbuchen: Übertrags-Hinweis (Quelle/Ziel + Betrag).
                Liest den strukturierten transferContext aus newValues (direction "in",
                neue Buchung) bzw. oldValues (direction "out", alte Buchung). */}
            {transferContext && (
                <Alert
                    type="success"
                    showIcon
                    style={{marginBottom: 16}}
                    message={
                        transferContext.direction === "out"
                            ? `Übertrag: ${renderAmount({amount: transferContext.transferredAmount, currency: transferContext.currency as "EUR" | "CHF" | "USD" | "GBP"})} auf Buchung ${transferContext.targetBookingNumber ?? "?"}${transferContext.targetCourseNumber ? ` (Kurs ${transferContext.targetCourseNumber})` : ""} übertragen`
                            : `Übertrag: ${renderAmount({amount: transferContext.transferredAmount, currency: transferContext.currency as "EUR" | "CHF" | "USD" | "GBP"})} aus Buchung ${transferContext.sourceBookingNumber ?? "?"}${transferContext.sourceCourseNumber ? ` (Kurs ${transferContext.sourceCourseNumber})` : ""} übertragen`
                    }
                />
            )}

            {/* Diff-Bereich:
                - Single-Sided (nur eine Seite hat Daten): action-fokussiertes Layout,
                  zeigt entweder "Hinzugefuegter Posten" (gruen) oder "Entfernter Posten"
                  (rot) als ScheduledPaymentsCard im Single-Action-Modus, plus eine
                  Info-Zeile mit dem (neuen/bisherigen) Buchungs-Bruttopreis.
                  Bei Events ohne scheduledPayments-Snapshot (z.B. BookingCreate)
                  faellt es auf das generische renderJsonValue zurueck.
                - Two-Sided: bisheriges Vorher/Nachher-Layout unveraendert. */}
            {hasAnyData ? (
                singleSidedSide === "new" ? (
                    <div>
                        {newScheduledPayments && newScheduledPayments.length > 0 ? (
                            <>
                                {/* Sonderfall: clearManuallyAdjusted (Schutz vor Neuberechnung
                                    aufgehoben). Backend setzt das Marker-Flag im Snapshot,
                                    Frontend zeigt einen blauen Info-Akzent statt der gruenen
                                    "Hinzugefuegt"-Optik — die Werte sind unveraendert, nur
                                    der Schutz-Status wurde umgeschaltet. */}
                                {newValues?.overrideCleared === true ? (
                                    <ScheduledPaymentsCard
                                        title="Schutz vor Neuberechnung aufgehoben"
                                        payments={newScheduledPayments}
                                        currencyShort={currencySettings.short}
                                        accentColor="blue"
                                        mode="single-action-info"
                                    />
                                ) : (
                                    <ScheduledPaymentsCard
                                        title={
                                            newScheduledPayments.length === 1
                                                ? "Hinzugefügter Posten"
                                                : `Hinzugefügte Posten (${newScheduledPayments.length})`
                                        }
                                        payments={newScheduledPayments}
                                        currencyShort={currencySettings.short}
                                        accentColor="green"
                                        mode="single-action-add"
                                    />
                                )}
                                {/* Info-Zeile nur im scheduledPayments-Pfad: im Fallback unten
                                    rendert renderJsonValue den Buchungs-Bruttopreis bereits selbst,
                                    daher wuerde die Zeile dort doppelt erscheinen.
                                    Bei overrideCleared schreibt das Backend keinen
                                    bookingGrossPrice — die Info-Zeile entfaellt also automatisch. */}
                                {typeof newValues?.bookingGrossPrice === "number" && (
                                    <div style={{marginTop: 12, color: "#666"}}>
                                        Neuer Buchungs-Bruttopreis:{" "}
                                        <strong style={{color: "#333"}}>
                                            {renderAmount({amount: newValues.bookingGrossPrice as number, currency: currencyCode})}
                                        </strong>
                                    </div>
                                )}
                            </>
                        ) : (
                            Object.keys(newFiltered).length > 0 && renderJsonValue(newFiltered, renderOptions)
                        )}
                    </div>
                ) : singleSidedSide === "old" ? (
                    <div>
                        {oldScheduledPayments && oldScheduledPayments.length > 0 ? (
                            <>
                                <ScheduledPaymentsCard
                                    title={
                                        oldScheduledPayments.length === 1
                                            ? "Entfernter Posten"
                                            : `Entfernte Posten (${oldScheduledPayments.length})`
                                    }
                                    payments={oldScheduledPayments}
                                    currencyShort={currencySettings.short}
                                    accentColor="red"
                                    mode="single-action-remove"
                                />
                                {typeof oldValues?.bookingGrossPrice === "number" && (
                                    <div style={{marginTop: 12, color: "#666"}}>
                                        Bisheriger Buchungs-Bruttopreis:{" "}
                                        <strong style={{color: "#333"}}>
                                            {renderAmount({amount: oldValues.bookingGrossPrice as number, currency: currencyCode})}
                                        </strong>
                                    </div>
                                )}
                            </>
                        ) : (
                            Object.keys(oldFiltered).length > 0 && renderJsonValue(oldFiltered, renderOptions)
                        )}
                    </div>
                ) : (
                    <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24}}>
                        {/* Vorher-Spalte */}
                        {hasOldData && (
                            <div>
                                <h4 style={{borderBottom: "1px solid #f0f0f0", paddingBottom: 8}}>Vorher</h4>
                                {Object.keys(oldFiltered).length > 0 && renderJsonValue(oldFiltered, renderOptions)}
                                {oldScheduledPayments && oldScheduledPayments.length > 0 && (
                                    <ScheduledPaymentsCard
                                        title="Posten (vorher)"
                                        payments={oldScheduledPayments}
                                        currencyShort={currencySettings.short}
                                        accentColor={hasNewData ? "red" : "default"}
                                    />
                                )}
                            </div>
                        )}
                        {/* Nachher-Spalte */}
                        {hasNewData && (
                            <div>
                                <h4 style={{borderBottom: "1px solid #f0f0f0", paddingBottom: 8}}>Nachher</h4>
                                {Object.keys(newFiltered).length > 0 && renderJsonValue(newFiltered, renderOptions)}
                                {newScheduledPayments && newScheduledPayments.length > 0 && (
                                    <ScheduledPaymentsCard
                                        title="Posten (nachher)"
                                        payments={newScheduledPayments}
                                        currencyShort={currencySettings.short}
                                        accentColor={hasOldData ? "green" : "default"}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )
            ) : (
                // Wenn die Beschreibung (z.B. "Kurseinheiten hinzugefügt: 1")
                // bereits die komplette Aussage des Events enthaelt, ist der
                // generische Empty-Hinweis irrefuehrend ("Keine Detail-Aenderungen"
                // — obwohl die relevanten technischen Felder bewusst gefiltert
                // wurden). In dem Fall zeigen wir nichts unterhalb des Headers.
                !cleanDescription && (
                    <Empty description="Keine Detail-Änderungen für diesen Eintrag" />
                )
            )}
        </div>
    );
};

export default memo(BookingHistoryDetails);
